/**
 * Header / menu alerts for pending workspace invitations, open Company link
 * proposals (for workspace owners), Company seat invitations, and the
 * "your workspace joined a Company" notice every member of that workspace
 * gets (ADR-022).
 *
 * The user's name and the "Workspace Controls" menu entry show a red dot while
 * they have invitations they haven't looked at yet. "Looked at" = landed on
 * Workspace Controls (where the invites are listed at the top). We track the
 * set of seen invitation IDs in localStorage so the dot stays cleared across
 * navigations and only re-appears for genuinely new invitations.
 *
 * Company seat invitations and link notices are shown in the account menu
 * itself (`AccountAlerts`). A seat invitation counts until it has been seen
 * there (same seen-set; ids never collide: vmi_ / cli_ / cmi_); a link notice
 * counts until the viewer dismisses it. Dismissals are per viewer, in this
 * browser — a convenience, never state anyone else relies on.
 *
 * localStorage isn't reactive on its own, so `markInvitationsSeen` and
 * `dismissCompanyLinkNotice` dispatch a window event the hooks listen to —
 * that way clearing the dot on one surface updates the header badge live.
 * Every storage access is wrapped: private windows and blocked site data
 * throw, and the alerts must still render.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from './useAuth';
import { useMyVaultInvitations } from './useWorkspaceTeam';
import {
  useMyCompanyLinkNotices,
  useMyCompanyLinkProposals,
  useMyCompanySeatInvitations,
} from './useCompanies';

const SEEN_KEY = 'fias.seen-invitation-ids';
const SEEN_EVENT = 'fias:invitations-seen';
const DISMISSED_NOTICES_KEY = 'fias.dismissed-company-link-notices';

function readIdSet(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(raw) ? new Set(raw as string[]) : new Set();
  } catch {
    return new Set();
  }
}

/** Adds ids to a stored set; true when anything was new. Storage failures are swallowed. */
function addToIdSet(key: string, ids: string[]): boolean {
  const set = readIdSet(key);
  let changed = false;
  for (const id of ids) {
    if (!set.has(id)) {
      set.add(id);
      changed = true;
    }
  }
  if (!changed) return false;
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    // Per-viewer convenience only; the alert simply reappears next load.
  }
  return true;
}

/** Mark the given invitation IDs as seen and notify any mounted count hooks. */
export function markInvitationsSeen(ids: string[]): void {
  if (typeof window === 'undefined' || ids.length === 0) return;
  if (addToIdSet(SEEN_KEY, ids)) window.dispatchEvent(new CustomEvent(SEEN_EVENT));
}

/** Hide one "<workspace> joined <Company>" notice for this viewer. */
export function dismissCompanyLinkNotice(invitationId: string): void {
  if (typeof window === 'undefined') return;
  if (addToIdSet(DISMISSED_NOTICES_KEY, [invitationId])) {
    window.dispatchEvent(new CustomEvent(SEEN_EVENT));
  }
}

/** Re-renders whenever a seen-set or dismissal changes. */
function useSeenTick(): number {
  const [seenTick, setSeenTick] = useState(0);
  useEffect(() => {
    const onSeen = () => setSeenTick((t) => t + 1);
    window.addEventListener(SEEN_EVENT, onSeen);
    return () => window.removeEventListener(SEEN_EVENT, onSeen);
  }, []);
  return seenTick;
}

/**
 * Number of pending workspace invitations and Company link proposals the user
 * hasn't seen yet — drives the Workspace Controls alert dot. Reactive:
 * refetches with the queries and re-reads the seen set whenever
 * `markInvitationsSeen` fires.
 */
export function useUnseenInvitationCount(): number {
  const { isAuthenticated } = useAuth();
  const { data: invitations } = useMyVaultInvitations({ enabled: isAuthenticated });
  // Company link proposals on workspaces the user OWNS count too — same
  // seen-set (ids never collide: vmi_ vs cli_), same landing page.
  const { data: proposals } = useMyCompanyLinkProposals({ enabled: isAuthenticated });
  const seenTick = useSeenTick();

  // seenTick is an intentional extra dependency: it forces a re-read of the
  // localStorage seen-set after markInvitationsSeen fires.
  return useMemo(() => {
    const seen = readIdSet(SEEN_KEY);
    return (
      (invitations ?? []).filter((i) => !seen.has(i.invitationId)).length +
      (proposals ?? []).filter((p) => !seen.has(p.invitationId)).length
    );
  }, [invitations, proposals, seenTick]);
}

/** Link notices this viewer has not dismissed. */
export function useCompanyLinkNotices() {
  const { isAuthenticated } = useAuth();
  const { data: notices } = useMyCompanyLinkNotices({ enabled: isAuthenticated });
  const seenTick = useSeenTick();
  return useMemo(() => {
    const dismissed = readIdSet(DISMISSED_NOTICES_KEY);
    return (notices ?? []).filter((n) => !dismissed.has(n.invitationId));
  }, [notices, seenTick]);
}

/**
 * Company alerts for the account menu: seat invitations not yet seen plus
 * link notices not yet dismissed.
 */
export function useCompanyAlertCount(): number {
  const { isAuthenticated, isGuest } = useAuth();
  // Company routes need a full account; a guest would only meet a refusal.
  const { data: seatInvitations } = useMyCompanySeatInvitations({
    enabled: isAuthenticated && !isGuest,
  });
  const notices = useCompanyLinkNotices();
  const seenTick = useSeenTick();
  return useMemo(() => {
    const seen = readIdSet(SEEN_KEY);
    return (seatInvitations ?? []).filter((i) => !seen.has(i.invitationId)).length + notices.length;
  }, [seatInvitations, notices, seenTick]);
}
